import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { EmployeeAPI } from 'src/services/api/employeeApi';
import { AttendenceDTO } from 'src/services/api/models/attendanceDto';
import { AxiosInstance, AxiosInstanceFormData } from 'src/services/axios/interceptors';
import { useGlobals } from 'src/services/contexts/appContext';
import ConsoleHelper from 'src/services/helpers/consoleHelper';
import { useIsMounted } from 'src/services/helpers/useIsMounted';
import userManager from 'src/services/identity/userManager';
import { RootStateType } from 'src/services/redux/reducers';
import AppLoading from 'src/shared/components/atoms/appLoading';

const SilentSignin = () => {
  const isMounted = useIsMounted();
  const { setLoggedInUser, setCheckInStatusUser } = useGlobals();
  const user = useSelector((state: RootStateType) => state.oidc.user);
  const { domain } = useSelector((state: RootStateType) => state.appState);
  const [loading, setLoading] = useState(false);

  const trySilentSigning = useCallback(async () => {
    setLoading(true);
    try {
      const silentResult = await userManager.signinSilent();
      if (isMounted()) {
        ConsoleHelper.log('Silent login success', silentResult);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.warn('Silent login failed');
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    if (!domain) return;
    AxiosInstance.defaults.baseURL = `https://${domain}.easystaff.se/api/v1`;
    AxiosInstanceFormData.defaults.baseURL = `https://${domain}.easystaff.se/api/v1`;
  }, [domain]);

  useEffect(() => {
    trySilentSigning();
  }, [trySilentSigning]);

  useEffect(() => {
    const fetchUser = async () => {
      const employeeAPI = new EmployeeAPI();
      const result = await employeeAPI.GetLoggedInUser();
      setLoggedInUser(result);
    };
    if (user && !user.expired && domain) {
      fetchUser().catch((error) => {
        ConsoleHelper.warn('Failed to fetch logged-in user', error);
      });
    }
  }, [user, domain, setLoggedInUser]);

  useEffect(() => {
    const fetchUser = async () => {
      const employeeAPI = new EmployeeAPI();
      const result = await employeeAPI.GetLoggedInUser();
      const checkInUser: AttendenceDTO = {
        userGuid: result.GUID,
        userImage: result.Image,
        firstName: result.Firstname,
        currentAttendence: result.CurrentAttendance,
        currentAbsence: result.CurrentAbsence,
        onDuty: result.OnDuty,
      };
      setCheckInStatusUser(checkInUser);
    };
    if (user && !user.expired && domain) {
      fetchUser().catch((error) => {
        ConsoleHelper.warn('Failed to fetch check-in status', error);
      });
    }
  }, [setCheckInStatusUser, user, domain]);

  useEffect(() => {
    if (user && !user.expired && !domain) {
      ConsoleHelper.warn('Authenticated session present but tenant domain is missing — clearing local session.');
      userManager.removeUser();
    }
  }, [user, domain]);

  return loading ? <AppLoading /> : null;
};

export default SilentSignin;
